import React from 'react';
import * as Sentry from '@sentry/react';
import { TryTeetshHeader } from 'components/TryTeetshHeader';
import { useGetShareCJ } from 'containers/CahierJournal/CJShare/useGetShareCJ';
import { Outlet, useParams } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import { DayColumn } from 'containers/CahierJournal/DayColumn';
import { format } from 'utils/time/format';
import { useCJSeances } from 'containers/CahierJournal/queries/useCJSeances';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { getMutualWorkingDays } from 'containers/utils';
import { CahierJournalView } from 'containers/CahierJournal/types';
import getDaysBetween from 'utils/time/getDaysBetween';
import { doesDayBelongToWorkingDays } from 'containers/CahierJournal/utils/doesDayBelongToWorkingDays';
import { getFromAndTo } from 'containers/CahierJournal/utils/getFromAndTo';
import ManualClassroomsProvider from 'containers/App/context/ClassroomsContext/ManualClassroomsContext';
import { FakeSchoolSchoolyearProvider } from 'containers/App/context/FakeSchoolSchoolyearProvider';
import FakeUserProvider from 'containers/App/context/FakeUserProvider';
import tw from 'twin.macro';
import { Toolbar } from 'containers/CahierJournal/Toolbar';
import ErrorFallback from 'components/Errors/ErrorFallback';
import { CJSeanceUpdateModal } from 'containers/CahierJournal/UpdateCJSeanceModal';
import { CreateCJSeanceModal } from 'containers/CahierJournal/CreateCJSeanceModal';
import getDateFromCJUrl from 'utils/time/getDateFromCJUrl';
import { useIsBelowBreakpoint } from 'components/ResponsiveSwitcher/useIsBelowSize';
import { RedirectToCurrentDay } from '../RedirectToCurrentDay';
import { CopyEventsToAnotherDayModal } from 'containers/CahierJournal/CopyEventsToAnotherDayModal';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { NotFoundPage } from 'components/ErrorPage/NotFoundPage';
import { useTranslation } from 'react-i18next';
import { useOnDragEnd } from 'containers/CahierJournal/queries/useOnDragEnd';
import Logo from 'assets/logo-white.png';
import { cjRoutes } from '../urls';
import ManualCustomFieldDefinitionsProvider from 'containers/App/context/EventCustomFieldDefinitionsContext/ManualCustomFieldDefinitionsProvider';
import { isOutsideSchoolyearRange } from '../utils/schoolyearRange';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';

export function CJShare() {
  const classrooms = useClassrooms();
  const mutualWorkingDays = getMutualWorkingDays(classrooms);
  const params = useParams<{
    view: CahierJournalView;
    year: string;
    month: string;
    day: string;
    userId: string;
    token: string;
  }>();

  const userId = params.userId as string;
  const token = params.token as string;
  const year = params.year as string;
  const month = params.month as string;
  const day = params.day as string;
  const view = params.view as CahierJournalView;
  const date = getDateFromCJUrl(year, month, day);
  const { guestMode } = useUser();

  const onDragEnd = useOnDragEnd('');
  const { schoolyear } = useSchoolSchoolyear();
  const { from, to } = getFromAndTo(view, date, mutualWorkingDays);
  const { isFetched, data = [] } = useCJSeances({
    from: format(from, 'fr-FR'),
    to: format(to, 'fr-FR'),
  });

  const isMobile = useIsBelowBreakpoint('md');
  if (isMobile && view !== 'day') {
    return <RedirectToCurrentDay />;
  }

  const days: Date[] = getDaysBetween(from, to).filter(d =>
    doesDayBelongToWorkingDays(d, mutualWorkingDays),
  );
  const isEmpty = data.length === 0;
  const isTimeIntervalOutsideSchoolyearRange = isOutsideSchoolyearRange(
    schoolyear,
    date,
  );
  const isOutsideSchoolyearRangeWithoutSeance =
    isEmpty && isTimeIntervalOutsideSchoolyearRange;

  return (
    <div tw="flex-grow bg-white h-full min-h-0">
      <div tw="flex flex-col flex-1 h-full">
        <div tw="p-3 bg-neutral-quiet border-b mb-2">
          <Toolbar
            previewLink={cjRoutes.previewShare({
              userId,
              token,
              view,
              date: from,
            })}
            date={from}
            view={view}
          />
        </div>
        <div tw="flex-grow h-full overflow-y-auto">
          <Sentry.ErrorBoundary
            fallback={({ error }) => <ErrorFallback error={error} />}
          >
            {guestMode?.permission !== 'read' && (
              <>
                <CJSeanceUpdateModal />
                <CreateCJSeanceModal />
                <CopyEventsToAnotherDayModal />
              </>
            )}
            <div
              css={[
                view === 'day' && tw`max-w-6xl mx-auto`,
                view !== 'day' && tw`px-3`,
                tw`flex justify-between overflow-x-auto pb-4 h-full flex-1 space-x-2`,
              ]}
            >
              <DragDropContext onDragEnd={onDragEnd}>
                {days.map(d => (
                  <DayColumn
                    key={format(d, 'fr-FR')}
                    eleves={[]}
                    plannerEvents={[]}
                    previewLink={cjRoutes.previewShare({
                      userId,
                      token,
                      view: 'day',
                      date: d,
                    })}
                    isWeekFetched={isFetched}
                    day={d}
                    view={view}
                    isFetched={isFetched}
                    disableHeaderActions={isOutsideSchoolyearRangeWithoutSeance}
                    hideColumnContent={isOutsideSchoolyearRangeWithoutSeance}
                  />
                ))}
              </DragDropContext>
            </div>
          </Sentry.ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

export function CJShareWrapper() {
  const params = useParams<{
    token: string;
    userId: string;
  }>();
  const userId = params.userId as string;
  const token = params.token as string;
  const { t } = useTranslation(['cahierJournal']);
  const { data, error } = useGetShareCJ(userId, token);
  if (error?.response?.status === 404) {
    return <NotFoundPage title={t('cahierJournal:partageIntrouvable')} />;
  }
  if (!data) return null;

  return (
    <div tw="flex flex-col h-screen overflow-hidden">
      <div className="dark">
        <TryTeetshHeader
          utmCampaign="share_cahier_journal"
          title={<img style={{ height: '30px' }} alt="Teetsh" src={Logo} />}
        />
      </div>
      <FakeUserProvider
        user={{
          id: userId,
          location: data.tz,
          locale: data.locale,
          country: data.country,
          boLabels: data.boLabels,
          referencielId: data.referentielId,
          fonts: {
            cj: {
              disableClassroomBar: false,
              displaylength: true,
              displaytags: true,
              displaymaterialssum: true,
              displayElevesGroups: false,
              displayTeacherName: false,
              displaybilan: false,
              font: 'Roboto',
              direction: 'below',
              size: 10,
              breakinsideavoid: false,
            },
          } as any,
          guestMode: {
            enabled: true,
            permission: data.permission,
          },
          enableSeanceSearchByDate: false,
        }}
      >
        <FakeSchoolSchoolyearProvider
          schoolyear={data.schoolyear}
          school={data.school}
        >
          <ManualCustomFieldDefinitionsProvider definitions={[]}>
            <ManualClassroomsProvider classrooms={data.classrooms}>
              <Outlet />
            </ManualClassroomsProvider>
          </ManualCustomFieldDefinitionsProvider>
        </FakeSchoolSchoolyearProvider>
      </FakeUserProvider>
    </div>
  );
}
